import { useMappedState } from "redux-react-hook";

import { GenericObject } from "src/library/utils";
import type { IStore } from "src/core/shared/store";
import { IAppSettings } from "src/core/shared/store/global/global.reducer";
import useAxios, { IApiResponse } from "src/core/shared/hooks/useAxios";
import {
  ERPOdoo,
  ERPRJL,
  ERPSeedless,
  ERPUnity,
  ERPZertus
} from "src/utils/constants";
import cacheMinutesToMilliseconds from "src/library/utils/time-helpers/cache-minutes-to-milliseconds";

type SettingApiType = "GET_CLIENT_SETTINGS" | "GET_SETTING" | "UPDATE_SETTING";
type SettingNameType = "platform" | "erp" | "cognito" | "priceMode" | "wrapperClasses" | "theme" | "terminology";

export type AwsRegionType = "eu-west-1" | "eu-west-2";

export type ERPType = typeof ERPSeedless | typeof ERPUnity | typeof ERPRJL | typeof ERPOdoo | typeof ERPZertus;

export interface ISettingsResponse<N = SettingNameType, S = GenericObject> {
  id: number;
  name: N;
  description: string;
  setting: S;
}

export interface IPlatformSetting {
  maintenance_mode: {
    roles: PlatformMaintananceModeRolesType[];
    message?: string;
  };
}

export type PlatformMaintananceModeRolesType = "admin" | "customer";

export interface IAwsSetting {
  cognito: ICognitoSetting;
  s3: IS3Setting;
}

interface ICognitoSetting {
  region: AwsRegionType;
  identityPoolId: string;
  userPoolId: string;
  clientId: string;
  group: string;
}

interface IS3Setting {
  region: AwsRegionType;
  bucket: string;
}

export type GetGlobalSettingsParamsType = SettingNameType | null;

export interface UpdateGlobalSettingParams<S = GenericObject> {
  name: SettingNameType;
  data: S;
}

export const GLOBAL_SETTINGS_CACHE_KEY = "global-settings";

export const globalSettingsApi = <R, P = any>(name: SettingApiType): [IApiResponse<R, any>, <C = P>(P: C) => void] => {
  switch (name) {
    case "GET_CLIENT_SETTINGS":
      return useAxios<R>(
        () => ({
          headers: false,
          url: `${process.env.REACT_APP_API_URL}/public/global-config/client/settings`,
          method: "GET",
          cache: {
            maxAge: cacheMinutesToMilliseconds(15),
            clearOnError: true,
            key: () => GLOBAL_SETTINGS_CACHE_KEY
          }
        }),
        name
      );

    case "GET_SETTING":
      return useAxios<R, P>(
        (settingName: string) => ({
          url: `${process.env.REACT_APP_API_URL}/core/global-config/${settingName}`,
          method: "GET"
        }),
        name
      );

    case "UPDATE_SETTING":
      return useAxios<R, UpdateGlobalSettingParams>(
        params => ({
          url: `${process.env.REACT_APP_API_URL}/core/global-config/${params.name}`,
          method: "PATCH",
          data: params.data
        }),
        name
      );

    default:
      throw new Error("errorsApi requires a name parameter");
  }
};

export const useGlobalSettingsApi = (): [IAppSettings | null, typeof globalSettingsApi] => {
  const { settings } = useMappedState(({ global }: IStore) => ({ settings: global.settings }));

  return [ settings, globalSettingsApi ];
};
